import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";
import { useWindowSize } from "usehooks-ts";

import { ProfileButton } from "@/components/doc/left-sidebar/public-profile/ProfileButton";
import { useStore } from "@/store";
import { SCREEN_SM } from "@/style/breakpoints";

import { NavSection, NavTree } from "../useSidebarNav";
import { useVisibleRowsCounts } from "../useVisibleRowsCounts";

import { Add } from "./Add";
import { EmptyFolderText } from "./EmptyFolderText";
import { FolderItem } from "./FolderItem";
import { ShowMoreButton } from "./ListItemButton";
import { RecentDocuments } from "./RecentDocuments";

import type { FolderFilesItem } from "@/api";

export type TreeItem = FolderFilesItem & {
  id: string;
  parentId: string;
  depth: number;
  isOpen?: boolean;
  isEditing?: boolean;
  files?: TreeItem[];
  index: number;
  type: "folder" | "document";
};

export const DocumentTree = observer(function DocumentTree() {
  const t = useTranslations("documentsSidebar");
  const { documentTree } = useStore();
  const { width: windowWidth } = useWindowSize();
  const { isDocsListExpanded } = documentTree;

  const isMobile = windowWidth < SCREEN_SM;

  const rootDocsCount = documentTree.root?.files?.length ?? 0;
  const { currentDocRootIndex } = documentTree;

  const { containerToFitRef, docsContainerRef, visibleDocsCount } = useVisibleRowsCounts({
    enabled: !isMobile,
    docs: {
      totalCount: rootDocsCount,
      isExpanded: isDocsListExpanded,
      minVisibleCount: currentDocRootIndex === undefined ? 0 : currentDocRootIndex + 1,
    },
  });

  if (!documentTree.root) {
    return null;
  }

  return (
    <NavTree aria-label={t("documentTreePanel")}>
      <div ref={containerToFitRef}>
        <RecentDocuments className="mt-1.5 p-1 first:mt-0" />
        <NavSection className="mt-1.5 p-1 first:mt-0">
          <FolderItem
            folder={documentTree.root}
            title={t("documentsFolder")}
            iconName="Documents"
            isReadOnly
            draggable={false}
            withDividerWhenOpen
            emptyFolder={<EmptyFolderText>{t("noDocuments")}</EmptyFolderText>}
            bottomComponent={
              visibleDocsCount !== undefined && rootDocsCount > visibleDocsCount ? (
                <ShowMoreButton
                  label={t("moreDocuments")}
                  onClick={() => documentTree.expandDocsList()}
                />
              ) : null
            }
            visibleFilesCount={visibleDocsCount}
            filesContainerRef={docsContainerRef}
            rightButton={
              <Add.DocumentButton folder={documentTree.root} title={t("newDocument")} index={0} />
            }
            isMobile={isMobile}
          />
        </NavSection>
        <NavSection className="mt-1.5 p-1">
          {documentTree.drafts && (
            <FolderItem
              folder={documentTree.drafts}
              title={t("draftsFolder")}
              iconName="Drafts"
              isReadOnly
              draggable={false}
              withDividerWhenOpen
              emptyFolder={<EmptyFolderText>{t("noDrafts")}</EmptyFolderText>}
              rightButton={
                <Add.DocumentButton folder={documentTree.drafts} title={t("newDraft")} />
              }
              isMobile={isMobile}
              hintKey="drafts"
            />
          )}
        </NavSection>
        <NavSection className="mt-1.5 p-[2px]">
          {documentTree.published && (
            <FolderItem
              folder={documentTree.published}
              title={t("publicProfile")}
              iconName="PublicDocs"
              isReadOnly
              draggable={false}
              withDividerWhenOpen
              emptyFolder={
                <Add.Document folder={documentTree.published} title={t("newPublished")} />
              }
              bottomComponent={
                <>
                  {documentTree.published.files?.length === 0 ? null : (
                    <Add.Document folder={documentTree.published} title={t("newPublished")} />
                  )}
                  <div className="mt-2.5 mb-1 mx-2.5 h-[1px] bg-block-border" />
                  <div className="p-2.5 text-gray-500 text-size-15 leading-[18px] tracking-tight">
                    {t("publicProfileDescription")}
                  </div>
                </>
              }
              rightButton={<ProfileButton />}
              isMobile={isMobile}
              hintKey="publicProfile"
            />
          )}
        </NavSection>
      </div>
      {documentTree.trash && (
        <NavSection className="mt-1.5 p-[2px]">
          <FolderItem
            folder={documentTree.trash}
            iconName="TrashFolder"
            className="py-[2px]"
            title={t("trashFolder")}
            isReadOnly="deep"
            draggable={false}
            childrenDroppable={false}
            sortable={false}
            isTrash
            emptyFolder={<EmptyFolderText>{t("noTrash")}</EmptyFolderText>}
            isMobile={isMobile}
            hintKey="trash"
          />
        </NavSection>
      )}
    </NavTree>
  );
});
